import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, TrashSimple, PencilSimple, DownloadSimple } from "@phosphor-icons/react";
import { useConfirmDialog } from "@/components/ConfirmDialog";
import { exportPorzionarioPdf } from "@/lib/exportPorzionario";

const CAT_LABELS = {
  proteici: "Proteici",
  amidacei: "Amidacei",
  frutta: "Frutta",
  lipidici: "Lipidici",
};

function toNum(v) {
  if (v === "" || v == null) return 0;
  const s = String(v).replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

const numToStr = (v) => (v === 0 || v == null || v === "") ? "" : String(v).replace(".", ",");

const emptyForm = { name: "", grams: "", protein_g: "", carbs_g: "", fat_g: "", secondChoice: false };

// Fixed macro description per gender × category, replacing the old
// per-portion kcal indicator. Values are the ones agreed with the coach
// (Metodo Funzionale) and do NOT come from the DB — they describe the
// target macro that the whole category is calibrated on.
const PORTION_MACRO_TEXT = {
  M: {
    proteici: "1 porzione ± 15 gr di proteine",
    amidacei: "1 porzione ± 15 gr di carboidrati complessi",
    frutta:   "1 porzione ± 10 gr di carboidrati semplici",
    lipidici: "1 porzione ± 10 gr di grassi",
  },
  F: {
    proteici: "1 porzione ± 10 gr di proteine",
    amidacei: "1 porzione ± 12 gr di carboidrati complessi",
    frutta:   "1 porzione ± 10 gr di carboidrati semplici",
    lipidici: "1 porzione ± 8 gr di grassi",
  },
};

export default function FoodEquivalents({ foodDb, gender = "M", isAdmin = false, onChanged, studio = null }) {
  const [tab, setTab] = useState("proteici");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFood, setEditingFood] = useState(null); // null when adding
  const [form, setForm] = useState(emptyForm);
  const { confirm, dialog } = useConfirmDialog();

  useEffect(() => {
    if (editingFood) {
      // Trailing " 2" marker means the food is flagged as second-choice.
      // Strip it from the displayed name and surface it as the checkbox
      // state so the admin can toggle without editing the string manually.
      const raw = editingFood.name || "";
      const m = raw.match(/^(.*?)\s+2\s*$/);
      setForm({
        name: m ? m[1] : raw,
        grams: numToStr(editingFood.grams),
        protein_g: numToStr(editingFood.protein_g),
        carbs_g: numToStr(editingFood.carbs_g),
        fat_g: numToStr(editingFood.fat_g),
        secondChoice: !!m,
      });
    } else {
      setForm(emptyForm);
    }
  }, [editingFood]);

  if (!foodDb) return null;

  const openAdd = () => { setEditingFood(null); setDialogOpen(true); };
  const openEdit = (f) => { setEditingFood(f); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || toNum(form.grams) <= 0) {
      toast.error("Nome e grammatura sono obbligatori");
      return;
    }
    // The " 2" marker is a canonical suffix — never store it twice even if
    // the admin manually typed it AND ticked the checkbox.
    const cleanName = form.name.trim().replace(/\s+2\s*$/, "");
    const finalName = form.secondChoice ? `${cleanName} 2` : cleanName;
    const body = {
      gender,
      category: editingFood?.category || tab,
      name: finalName,
      grams: toNum(form.grams),
      protein_g: toNum(form.protein_g),
      carbs_g: toNum(form.carbs_g),
      fat_g: toNum(form.fat_g),
    };
    try {
      if (editingFood) {
        await api.patch(`/nutrition/foods/${editingFood.food_id}`, body);
        toast.success("Alimento aggiornato");
      } else {
        await api.post("/nutrition/foods", body);
        toast.success("Alimento aggiunto");
      }
      setDialogOpen(false);
      setEditingFood(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio alimento");
    }
  };

  const deleteFood = async (f) => {
    const ok = await confirm({
      title: "Eliminare alimento?",
      description: `«${f.name}» sarà rimosso dal porzionario ${gender === "F" ? "femminile" : "maschile"}.`,
      confirmLabel: "Elimina",
    });
    if (!ok) return;
    try {
      await api.delete(`/nutrition/foods/${f.food_id}`);
      toast.success("Alimento eliminato");
      onChanged?.();
    } catch (e) { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="border-2 border-[color:var(--border)] rounded-2xl p-5 sm:p-6 bg-[color:var(--bg-primary)]" data-testid="food-equivalents">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="label-tiny">Porzionario</div>
          <div className="font-serif text-2xl leading-tight mt-1">Equivalenti alimentari</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            data-testid="export-porzionario-btn"
            size="sm"
            variant="outline"
            onClick={() => exportPorzionarioPdf({ foodDb, studio, gender })}
            className="rounded-full text-xs h-8"
            title="Scarica porzionario in PDF"
          >
            <DownloadSimple size={12} weight="bold" className="mr-1" /> PDF
          </Button>
          {isAdmin && (
            <Button data-testid="add-food-btn" size="sm" variant="outline" onClick={openAdd} className="rounded-full text-xs h-8">
              <Plus size={12} weight="bold" className="mr-1" /> Alimento
            </Button>
          )}
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent gap-1.5 p-0 h-auto flex flex-wrap">
          {Object.keys(CAT_LABELS).map((k) => (
            <TabsTrigger
              key={k}
              value={k}
              className="rounded-full px-4 py-1.5 text-sm font-medium border-2 border-[color:var(--border)] data-[state=active]:bg-[color:var(--text-primary)] data-[state=active]:text-white data-[state=active]:border-[color:var(--text-primary)] transition-colors"
            >
              {CAT_LABELS[k]}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.keys(CAT_LABELS).map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            <div
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full mb-3"
              style={{ background: "var(--bg-secondary)", color: "var(--text-primary)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--accent)" }} />
              <span data-testid={`porzione-macro-${k}`}>
                {PORTION_MACRO_TEXT[gender === "F" ? "F" : "M"]?.[k] || ""}
              </span>
            </div>
            <ul className="text-[15px] max-h-96 overflow-y-auto pr-1 rounded-xl border border-[color:var(--border)] divide-y divide-[color:var(--border)]" style={{ background: "var(--bg-secondary)" }}>
              {foodDb.foods?.[k]?.map((f) => {
                // Whole eggs are expressed in units (1/2), not grams.
                const isEggUnit = /^uov[oa]\s+inter/i.test(f.name || "");
                const unitLabel = isEggUnit
                  ? (f.grams === 1 ? "uovo" : "uova")
                  : "g";
                const valueLabel = isEggUnit ? `${f.grams} ${unitLabel}` : `${f.grams}${unitLabel}`;
                // Render trailing " 2" marker as a bold superscript ("second-choice").
                const nameParts = (f.name || "").match(/^(.*?)\s+2\s*$/);
                const displayName = nameParts ? nameParts[1] : f.name;
                const hasSecondChoice = !!nameParts;
                return (
                <li
                  key={f.food_id || `${f.name}-${f.grams}`}
                  className="group flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[color:var(--bg-primary)] transition-colors"
                >
                  <span className="min-w-0 truncate font-medium text-[color:var(--text-primary)]">
                    {displayName}
                    {hasSecondChoice && (
                      <sup className="font-bold ml-0.5 text-[0.7em] leading-none align-super" data-testid="second-choice-marker">
                        2
                      </sup>
                    )}
                  </span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    {isAdmin && f.food_id && (
                      <>
                        <button
                          type="button"
                          data-testid={`edit-food-${f.food_id}`}
                          onClick={() => openEdit(f)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
                          title="Modifica alimento"
                        >
                          <PencilSimple size={14} weight="bold" />
                        </button>
                        <button
                          type="button"
                          data-testid={`delete-food-${f.food_id}`}
                          onClick={() => deleteFood(f)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-red-700"
                          title="Elimina alimento"
                        >
                          <TrashSimple size={14} weight="bold" />
                        </button>
                      </>
                    )}
                    <span
                      className="tabular-nums text-sm font-semibold px-2.5 py-0.5 rounded-full"
                      style={{ background: "var(--bg-primary)", color: "var(--text-primary)", border: "1px solid var(--border)" }}
                    >
                      {valueLabel}
                    </span>
                  </span>
                </li>
                );
              })}
            </ul>
          </TabsContent>
        ))}
      </Tabs>

      {/* Legend — matches the original Excel notation */}
      <div
        className="mt-4 rounded-xl border border-[color:var(--border)] p-3 text-[11px] leading-relaxed"
        style={{ background: "var(--bg-secondary)", color: "var(--text-secondary)" }}
        data-testid="food-legend"
      >
        <div className="label-tiny mb-2" style={{ color: "var(--text-primary)" }}>Legenda</div>
        <div className="grid gap-1.5 sm:grid-cols-1">
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">*</span>
            <span>cereali senza glutine</span>
          </div>
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">**</span>
            <span>alimenti limitati al post-workout (ove previsto)</span>
          </div>
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">#</span>
            <span>frutti che possono essere consumati anche alla fine di un pasto completo</span>
          </div>
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">2</span>
            <span>alimenti di seconda scelta</span>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditingFood(null); }}>
        <DialogContent data-testid="food-dialog" className="bg-[color:var(--bg-primary)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingFood ? "Modifica alimento" : "Nuovo alimento"} — {CAT_LABELS[editingFood?.category || tab]}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="label-tiny">Nome</Label>
              <Input data-testid="food-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Salmone affumicato" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tiny">Grammatura (g)</Label>
                <Input data-testid="food-grams" inputMode="decimal" value={form.grams} onChange={(e) => setForm({ ...form, grams: e.target.value })} required />
              </div>
              <div>
                <Label className="label-tiny">Proteine (g)</Label>
                <Input data-testid="food-protein" inputMode="decimal" value={form.protein_g} onChange={(e) => setForm({ ...form, protein_g: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Carboidrati (g)</Label>
                <Input data-testid="food-carbs" inputMode="decimal" value={form.carbs_g} onChange={(e) => setForm({ ...form, carbs_g: e.target.value })} />
              </div>
              <div>
                <Label className="label-tiny">Grassi (g)</Label>
                <Input data-testid="food-fat" inputMode="decimal" value={form.fat_g} onChange={(e) => setForm({ ...form, fat_g: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                data-testid="food-second-choice"
                id="food-second-choice"
                checked={!!form.secondChoice}
                onCheckedChange={(v) => setForm({ ...form, secondChoice: !!v })}
              />
              <Label htmlFor="food-second-choice" className="text-xs font-medium cursor-pointer select-none">
                Segna come alimento di seconda scelta <span className="text-[color:var(--text-secondary)]">(marker ²)</span>
              </Label>
            </div>
            <div className="text-[10px] text-[color:var(--text-secondary)] italic">
              Le calorie sono calcolate automaticamente: P×4 + C×4 + G×9.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annulla</Button>
              <Button type="submit" data-testid="save-food-btn" className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white">
                {editingFood ? "Salva modifiche" : "Aggiungi"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {dialog}
    </div>
  );
}
